/* Step 2: Customer details form for document stepper
   Updated: Replaced AddressAutocomplete with plain inputs (street, housenumber, addition).
   Address is built from street + housenumber + addition when saving. */

import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Card, CardContent } from '@/components/ui/card';
import { User, Mail, Phone, MapPin, FileText, CalendarDays } from 'lucide-react';
import type { CustomerData, DocumentType } from './types';

interface CustomerStepProps {
  customer: CustomerData;
  onChange: (customer: CustomerData) => void;
  documentType: DocumentType;
  errors: Record<string, string>;
}

export default function CustomerStep({ customer, onChange, documentType, errors }: CustomerStepProps) {
  const update = (field: keyof CustomerData, value: string) => {
    onChange({ ...customer, [field]: value });
  };

  return (
    <div className="max-w-2xl mx-auto space-y-6">
      <Card>
        <CardContent className="p-6 space-y-5">
          <div className="flex items-center gap-2 mb-2">
            <User className="w-4 h-4 text-jv-primary" />
            <h3 className="text-sm font-semibold text-gray-700">Contactgegevens</h3>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="name" className="text-xs font-medium text-gray-500">
              Naam <span className="text-red-500">*</span>
            </Label>
            <Input
              id="name"
              placeholder="Volledige naam"
              value={customer.name}
              onChange={(e) => update('name', e.target.value)}
              className={errors.name ? 'border-red-400' : ''}
            />
            {errors.name && <p className="text-xs text-red-500">{errors.name}</p>}
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label htmlFor="email" className="text-xs font-medium text-gray-500">
                <Mail className="w-3 h-3 inline mr-1" />
                E-mail <span className="text-red-500">*</span>
              </Label>
              <Input
                id="email"
                type="email"
                placeholder="email@voorbeeld.nl"
                value={customer.email}
                onChange={(e) => update('email', e.target.value)}
                className={errors.email ? 'border-red-400' : ''}
              />
              {errors.email && <p className="text-xs text-red-500">{errors.email}</p>}
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="phone" className="text-xs font-medium text-gray-500">
                <Phone className="w-3 h-3 inline mr-1" />
                Telefoon
              </Label>
              <Input
                id="phone"
                placeholder="06-12345678"
                value={customer.phone}
                onChange={(e) => update('phone', e.target.value)}
              />
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="p-6 space-y-5">
          <div className="flex items-center gap-2 mb-2">
            <MapPin className="w-4 h-4 text-jv-primary" />
            <h3 className="text-sm font-semibold text-gray-700">Adresgegevens</h3>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="street" className="text-xs font-medium text-gray-500">Straatnaam</Label>
            <Input
              id="street"
              placeholder="Kerkstraat"
              value={customer.street || ''}
              onChange={(e) => update('street', e.target.value)}
            />
          </div>

          <div className="grid grid-cols-3 gap-4">
            <div className="space-y-1.5">
              <Label htmlFor="housenumber" className="text-xs font-medium text-gray-500">Huisnummer</Label>
              <Input
                id="housenumber"
                placeholder="12"
                value={customer.housenumber || ''}
                onChange={(e) => update('housenumber', e.target.value)}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="addition" className="text-xs font-medium text-gray-500">Toevoeging</Label>
              <Input
                id="addition"
                placeholder="A"
                value={customer.housenumber_addition || ''}
                onChange={(e) => update('housenumber_addition', e.target.value)}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="postal_code" className="text-xs font-medium text-gray-500">Postcode</Label>
              <Input
                id="postal_code"
                placeholder="1234 AB"
                value={customer.postal_code}
                onChange={(e) => update('postal_code', e.target.value)}
              />
            </div>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="city" className="text-xs font-medium text-gray-500">Plaats</Label>
            <Input
              id="city"
              placeholder="Plaatsnaam"
              value={customer.city}
              onChange={(e) => update('city', e.target.value)}
            />
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="p-6 space-y-5">
          <div className="flex items-center gap-2 mb-2">
            <FileText className="w-4 h-4 text-jv-primary" />
            <h3 className="text-sm font-semibold text-gray-700">Aanvullend</h3>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="notes" className="text-xs font-medium text-gray-500">Opmerkingen</Label>
            <Textarea
              id="notes"
              placeholder="Eventuele opmerkingen of notities..."
              value={customer.notes}
              onChange={(e) => update('notes', e.target.value)}
              rows={3}
            />
          </div>

          {documentType === 'quote' && (
            <div className="space-y-1.5">
              <Label htmlFor="valid_until" className="text-xs font-medium text-gray-500">
                <CalendarDays className="w-3 h-3 inline mr-1" />
                Offerte geldig tot
              </Label>
              <Input
                id="valid_until"
                type="date"
                value={customer.valid_until}
                onChange={(e) => update('valid_until', e.target.value)}
                min={new Date().toISOString().split('T')[0]}
              />
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
